import {useEffect, useRef} from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error("Turnstile failed to load"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

/**
 * Cloudflare Turnstile bot check. Hidden unless Cloudflare needs the visitor
 * to interact ("interaction-only"); then it shows its challenge. Calls onToken(token) when solved and
 * onToken("") when the token expires or errors.
 */
export const Turnstile = ({siteKey, onToken, resetSignal}) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!siteKey) return undefined;
        let cancelled = false;

        loadTurnstile()
            .then((turnstile) => {
                if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    appearance: "interaction-only",
                    callback: (token) => onTokenRef.current(token),
                    "expired-callback": () => onTokenRef.current(""),
                    "error-callback": () => onTokenRef.current(""),
                });
            })
            .catch(() => onTokenRef.current(""));

        return () => {
            cancelled = true;
            if (widgetIdRef.current !== null && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch {
                    // ignore
                }
            }
            widgetIdRef.current = null;
        };
    }, [siteKey]);

    // A token can be used only once: get a fresh one after each attempt.
    useEffect(() => {
        if (!resetSignal || widgetIdRef.current === null || !window.turnstile) return;
        onTokenRef.current("");
        try {
            window.turnstile.reset(widgetIdRef.current);
        } catch {
            // ignore
        }
    }, [resetSignal]);

    return <div ref={containerRef}/>;
};
